import { useMemo } from "react";
import { useCachedQuery } from "./useCachedQuery";

// Per-customer rollup from /api/admin/tool-usage. Mirrors ToolCallUsageView
// on the backend.
export type AdminToolUsage = {
  ownerEmail: string;
  totalCount: number;
  successCount: number;
  errorCount: number;
  // IP-314: sub-tools rejected locally as unconnected (non-billable). A subset
  // of errorCount. Forwarded calls = totalCount - unconnectedCount.
  unconnectedCount: number;
  tenantCount: number;
};

export type AdminToolUsageFilter = {
  start: Date;
  end: Date;
  customerEmail?: string | undefined;
  // IP-314: optional provider filter (e.g. "composio").
  provider?: string | undefined;
};

type FetchStatus = "loading" | "ready" | "error";

const buildUrl = (filter: AdminToolUsageFilter): string => {
  const params = new URLSearchParams();
  params.set("start", filter.start.toISOString());
  params.set("end", filter.end.toISOString());
  if (filter.customerEmail) params.set("customerEmail", filter.customerEmail);
  if (filter.provider) params.set("provider", filter.provider);
  return `/api/admin/tool-usage?${params.toString()}`;
};

type Result = {
  status: FetchStatus;
  rows: ReadonlyArray<AdminToolUsage>;
  error: string | null;
  isFetching: boolean;
  refresh: () => void;
};

// cacheResource is decided by the page: only the default view (last-30d
// preset, all customers) is worth persisting, and only the page knows
// which preset is selected.
export const useAdminToolUsage = (
  filter: AdminToolUsageFilter,
  cacheResource: string | null = null,
): Result => {
  // Snapshot the dependencies so passing a fresh filter object every render
  // doesn't re-fetch endlessly. Dates compared by epoch ms.
  const startKey = filter.start.getTime();
  const endKey = filter.end.getTime();
  const emailKey = filter.customerEmail ?? "";
  const providerKey = filter.provider ?? "";

  const url = useMemo(
    () =>
      buildUrl({
        start: new Date(startKey),
        end: new Date(endKey),
        customerEmail: emailKey || undefined,
        provider: providerKey || undefined,
      }),
    [startKey, endKey, emailKey, providerKey],
  );

  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<ReadonlyArray<AdminToolUsage>> => {
      const res = await fetch(url, { credentials: "include", signal });
      if (!res.ok) throw new Error(`fetch failed (${res.status})`);
      return (await res.json()) as ReadonlyArray<AdminToolUsage>;
    },
    [url],
  );

  const { status, data, error, isFetching, refetch } = useCachedQuery<
    ReadonlyArray<AdminToolUsage>
  >({
    cacheResource,
    queryKey: url,
    fetcher,
  });

  return {
    status,
    rows: data ?? [],
    error,
    isFetching,
    refresh: () => void refetch(),
  };
};
